/**
 * The people a reader can register, as dropdown options.
 *
 * The choice used to live on the start step, before the form describing the
 * person was ever shown. It now sits on register-entity, directly above that
 * form: choosing someone prefills their reserved fields, and "Create New"
 * leaves them blank for the reader to fill in.
 *
 * Which list applies is decided by role — a team rep picks a team official,
 * a player picks from their own family — so the caller passes the right one
 * and this only shapes it.
 */

/** The person fields an option needs. Structural, so both the family and
 * team-official payloads satisfy it. */
export interface IdentityPerson {
  id?: string | null
  uid?: string | null
  name_first?: string | null
  name_last?: string | null
  /** The row's fallback second line, for someone with no stats to show. */
  email?: string | null
  /** Stats rendered as the icon row. All optional — a row shows what it has. */
  birth_date?: string | null
  /** Centimetres, as the API sends it; `Bio` converts to feet and inches. */
  height?: number | null
  /** Pounds. */
  weight?: number | null
}

/**
 * One dropdown row.
 *
 * `label` is a plain string on purpose even though the row renders as two
 * lines: antd matches the typed query against this value, so it must contain
 * everything the reader might search by — name AND email. The two-line
 * presentation is built from `person` at render time.
 */
export interface IdentityOptionItem {
  value: string
  label: string
  /** The person this row stands for; null on the create-new row. */
  person: IdentityPerson | null
}

/**
 * Sentinel id for "not one of these — collect the details from the form".
 *
 * The same value the flow has always used for a not-yet-created person, so the
 * submit path's existing `id !== 'new'` checks keep working unchanged.
 */
export const NEW_IDENTITY_ID = 'new'

/** The person's display name, or a stable fallback so no row renders blank. */
export const identityLabel = (person: IdentityPerson): string => {
  const name = [person.name_first, person.name_last]
    .filter((part) => Boolean(part && String(part).trim()))
    .join(' ')
    .trim()
  return name || 'Unnamed'
}

/**
 * The row's email line, shown whenever there is one.
 *
 * Sits alongside the stat icons rather than replacing them: the stats say who
 * the person is physically, the email says which account they are — and for
 * two family members with the same name, the email is what tells them apart.
 * Empty when unknown, so nothing renders rather than a blank line.
 */
export const identitySubtitle = (person: IdentityPerson): string =>
  person.email && String(person.email).trim() ? String(person.email).trim() : ''

/** Whether this person has any of the stats the icon row renders. */
export const hasIdentityStats = (person: IdentityPerson): boolean =>
  Boolean(person.birth_date || person.height || person.weight)

/**
 * The string antd matches a typed query against.
 *
 * Includes the email, so typing one finds the person even though the name is
 * what is displayed most prominently.
 */
export const identitySearchText = (person: IdentityPerson): string =>
  [identityLabel(person), person.email].filter(Boolean).join(' ')

/** A person's id, preferring `uid` — the same precedence the team lists use. */
export const identityPersonId = (
  person: IdentityPerson | null | undefined
): string | undefined => person?.uid ?? person?.id ?? undefined

/**
 * Build the dropdown options, with the create-new row last.
 *
 * The create-new row is always offered, even when the reader has people: they
 * may be registering someone new. People with no resolvable id are dropped —
 * an option that cannot be submitted is worse than one that is absent.
 */
export const buildIdentityOptions = (
  people: IdentityPerson[] | null | undefined,
  isTeamRole: boolean
): IdentityOptionItem[] => {
  const options: IdentityOptionItem[] = []

  for (const person of people ?? []) {
    const value = identityPersonId(person)
    if (!value) {
      continue
    }
    options.push({ value, label: identitySearchText(person), person })
  }

  options.push({
    value: NEW_IDENTITY_ID,
    label: isTeamRole ? 'Create a New Team Official' : 'Create a New Player',
    person: null,
  })

  return options
}

/** Find the person a dropdown value refers to, or null for create-new. */
export const findIdentityPerson = <T extends IdentityPerson>(
  people: T[] | null | undefined,
  value: string | null | undefined
): T | null => {
  if (!value || value === NEW_IDENTITY_ID) {
    return null
  }
  return (
    (people ?? []).find((person) => identityPersonId(person) === value) ?? null
  )
}
